import type { GridSelection, Rectangle } from "@glideapps/glide-data-grid";
import type {
  IAiSpreadsheetCell,
  IAiSpreadsheetLoadedSheet,
  IAiSpreadsheetMergedRange,
  IAiSpreadsheetRegion,
  IAiSpreadsheetRegionColumnSetting,
  IAiSpreadsheetRegionInput,
} from "@/payroll/ai-script/types";
import { getAiSpreadsheetCellDisplayValue } from "./cell-display-value";

export type SpreadsheetRegionDraft = IAiSpreadsheetRegionInput;

export interface SpreadsheetRegionBounds {
  maxRow: number;
  maxCol: number;
}

const hasDisplayedValue = (cell: IAiSpreadsheetCell | undefined) =>
  !!cell && getAiSpreadsheetCellDisplayValue(cell) !== "";

/**
 * `declared_type` is a deliberate user override, not a cached guess, so a fresh
 * proposal leaves it null and lets the API's column_type_policy read the type off
 * the displayed record cells. Inferring here would freeze a mixed column as `text`
 * on save and permanently suppress that policy.
 */
const buildColumnSettings = (
  startCol: number,
  endCol: number,
): Record<string, IAiSpreadsheetRegionColumnSetting> =>
  Object.fromEntries(
    Array.from({ length: endCol - startCol + 1 }, (_, offset) => [
      String(startCol + offset),
      { filterable: true, sortable: true, declared_type: null },
    ]),
  );

const normalizeRectangle = (range: Rectangle) => ({
  startCol: range.x + 1,
  endCol: range.x + range.width,
  headerRow: range.y + 1,
  recordEndRow: range.y + range.height,
});

const findContinuousRange = (
  sheet: IAiSpreadsheetLoadedSheet,
  row: number,
  col: number,
) => {
  let maxRow = row;
  let maxCol = col;
  const rowsWithValue = new Set<number>();
  const columnsWithValue = new Set<number>();

  for (const cell of sheet.cells) {
    maxRow = Math.max(maxRow, cell.row);
    maxCol = Math.max(maxCol, cell.col);
    if (!hasDisplayedValue(cell)) continue;
    rowsWithValue.add(cell.row);
    columnsWithValue.add(cell.col);
  }

  const rowIsBlank = (candidate: number) => !rowsWithValue.has(candidate);
  const colIsBlank = (candidate: number) => !columnsWithValue.has(candidate);

  let startRow = row;
  let endRow = row;
  let startCol = col;
  let endCol = col;

  while (startRow > 1 && !rowIsBlank(startRow - 1)) startRow -= 1;
  while (endRow < maxRow && !rowIsBlank(endRow + 1)) endRow += 1;
  while (startCol > 1 && !colIsBlank(startCol - 1)) startCol -= 1;
  while (endCol < maxCol && !colIsBlank(endCol + 1)) endCol += 1;

  return { startCol, endCol, headerRow: startRow, recordEndRow: endRow };
};

export const proposeSpreadsheetRegion = (
  sheet: IAiSpreadsheetLoadedSheet,
  selection: GridSelection,
): SpreadsheetRegionDraft | null => {
  const range = selection.current?.range;
  if (!range) return null;

  const limits =
    range.width === 1 && range.height === 1
      ? findContinuousRange(sheet, range.y + 1, range.x + 1)
      : normalizeRectangle(range);
  const recordStartRow = limits.headerRow + 1;

  return {
    header_row: limits.headerRow,
    record_start_row: recordStartRow,
    record_end_row: Math.max(recordStartRow, limits.recordEndRow),
    start_col: limits.startCol,
    end_col: limits.endCol,
    column_settings: buildColumnSettings(limits.startCol, limits.endCol),
  };
};

// Resizing keeps whatever the user already chose for the columns that survive the
// resize; only newly covered columns take the default. Rebuilding wholesale would
// silently discard their declared types and filter/sort toggles.
export const updateSpreadsheetRegionLimits = (
  draft: SpreadsheetRegionDraft,
  limits: Pick<
    SpreadsheetRegionDraft,
    "header_row" | "record_end_row" | "start_col" | "end_col"
  >,
): SpreadsheetRegionDraft => ({
  ...draft,
  ...limits,
  record_start_row: limits.header_row + 1,
  column_settings: Object.fromEntries(
    Object.entries(buildColumnSettings(limits.start_col, limits.end_col)).map(
      ([columnKey, defaults]) => [
        columnKey,
        draft.column_settings[columnKey] ?? defaults,
      ],
    ),
  ),
});

interface ValidateSpreadsheetRegionDraftOptions {
  regions?: IAiSpreadsheetRegion[];
  editedRegionId?: string;
  mergedRanges?: IAiSpreadsheetMergedRange[];
  bounds?: SpreadsheetRegionBounds;
}

export const validateSpreadsheetRegionDraft = (
  draft: SpreadsheetRegionDraft,
  {
    regions = [],
    editedRegionId,
    mergedRanges = [],
    bounds,
  }: ValidateSpreadsheetRegionDraftOptions = {},
): string[] => {
  const errors: string[] = [];
  // Stated in the units each is entered in: rows as numbers, columns as letters.
  if (draft.header_row < 1) {
    errors.push("The header row must be row 1 or later.");
  }
  if (draft.start_col < 1) {
    errors.push("The first column must be column A or later.");
  }
  if (draft.record_end_row < draft.record_start_row) {
    errors.push(
      "The region must have one header row and one or more record rows.",
    );
  }
  if (draft.end_col < draft.start_col) {
    errors.push("The last column must be on or after the first column.");
  }
  if (
    bounds &&
    (draft.record_end_row > bounds.maxRow || draft.end_col > bounds.maxCol)
  ) {
    errors.push("The region extends beyond the sheet cells.");
  }

  const overlaps = regions.some(
    (region) =>
      region.id !== editedRegionId &&
      region.validation.valid &&
      draft.header_row <= region.record_end_row &&
      draft.record_end_row >= region.header_row &&
      draft.start_col <= region.end_col &&
      draft.end_col >= region.start_col,
  );
  if (overlaps) errors.push("The region overlaps another table region.");

  // Mirrors AiSpreadsheetRegion#intersects_merged_range? — any intersection at all
  // is rejected, not just one that straddles a boundary.
  const intersectsMergedRange = mergedRanges.some(
    (range) =>
      draft.header_row <= range.end_row &&
      draft.record_end_row >= range.start_row &&
      draft.start_col <= range.end_col &&
      draft.end_col >= range.start_col,
  );
  if (intersectsMergedRange) {
    errors.push("The region crosses a merged cell range.");
  }

  return errors;
};

export const spreadsheetRegionHighlightRange = (
  region: SpreadsheetRegionDraft,
): Rectangle => ({
  x: region.start_col - 1,
  y: region.header_row - 1,
  width: region.end_col - region.start_col + 1,
  height: region.record_end_row - region.header_row + 1,
});
